import type { AdminAction, Row } from '@contract';
import { FileExportIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { callAdmin } from '@/lib/api';
import { invalidate } from '@/lib/query';
import { useCan } from '@/lib/staff-context';
import { toast } from '@/lib/toast';

/**
 * Exports exactly what the list shows, with its filters. The server builds
 * the CSV (nothing is masked in an export) and logs it; a large one finishes in the background and
 * shows on the Exports page.
 */
export function ExportButton({
  source,
  args,
}: Readonly<{ source: AdminAction; args: Row }>) {
  const canExport = useCan('exports.create');
  const create = useMutation({
    mutationFn: () => callAdmin('exports.create', { source, args }),
    onSuccess: async (result) => {
      await invalidate('exports.list');
      if (result.status === 'ready' && result.url) {
        toast.success(
          `${result.row_count} rows exported${result.truncated ? ' (the most one file holds)' : ''}.`,
        );
        window.location.assign(result.url);
        return;
      }
      if (result.status === 'building') {
        toast.info(
          `${result.row_count} rows are being written. Download them from Exports.`,
        );
        return;
      }
      toast.error('The export could not be written. Try again.');
    },
    onError: (error) => toast.error(error.message),
  });

  if (!canExport) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={create.isPending}
      onClick={() => create.mutate()}
    >
      <HugeiconsIcon icon={FileExportIcon} size={16} />
      {create.isPending ? 'Exporting…' : 'Export CSV'}
    </Button>
  );
}
